import {findLast} from 'lodash';

import {partitionDataGroupByMessageUid} from '../../helpers/models/messageHelpers';
import {AccessModel} from '../../models/api/accessModel';
import {ActivityModel} from '../../models/api/activityModel';
import {LoadedMessageModel, MessageVisualStatusesEnum} from '../../models/api/messageModel';
import {PreferencesModel} from '../../models/api/preferencesModel';
import {TagModel} from '../../models/api/tagModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {VacationResponderModel} from '../../models/api/vacationResponderModel';
import {Dispatch, StateGetter} from '../../store/appStore';
import {ThunkAsyncAction} from '../../store/appThunk';
import {deleteActivity} from '../../store/data/activities/activitiesActions';
import {deleteMessageAction} from '../../store/data/messages/messagesActions';
import {deleteTagAction} from '../../store/data/tags/tagsActions';
import {deleteVacationResponderAction} from '../../store/data/vacationResponders/vacationRespondersActions';
import {
  accessLockKey,
  buildActivityLockKey,
  buildMessageLockKey,
  buildTagLockKey,
  buildTeammateLockKey,
  buildVacationResponderLockKey,
  preferencesLockKey,
} from '../locks/resourceLocks';
import {DataGroupGetter, findQueue, ModelGetter, TaskQueueItem} from './taskQueues';

/**
 * @file
 * These queues are part of the legacy task queue system, which we plan to deprecate because it has known
 * issues. Do not adopt this pattern for new resources.
 */

export function findPreferencesQueue(dispatch: Dispatch, getState: StateGetter) {
  const queueName = preferencesLockKey;
  const modelGetter: ModelGetter<PreferencesModel> = (dataGroup, dataState) =>
    dataGroup.preferences || dataState.preferences;
  const dataGroupGetter: DataGroupGetter<PreferencesModel> = (model) => ({preferences: model});

  return findQueue(dispatch, getState, queueName, modelGetter, dataGroupGetter);
}

export function findAccessQueue(dispatch: Dispatch, getState: StateGetter) {
  const queueName = accessLockKey;
  const modelGetter: ModelGetter<AccessModel> = (dataGroup, dataState) =>
    dataGroup.access || dataState.access;
  const dataGroupGetter: DataGroupGetter<AccessModel> = (model) => ({access: model});

  return findQueue(dispatch, getState, queueName, modelGetter, dataGroupGetter);
}

export function findTeammateQueue(dispatch: Dispatch, getState: StateGetter, teammateId: number) {
  const queueName = buildTeammateLockKey(teammateId);
  const modelGetter: ModelGetter<TeammateModel> = (dataGroup, dataState) => {
    const teammate = findLast(dataGroup.teammates, (t) => t.id === teammateId);
    return teammate || dataState.teammates[teammateId];
  };
  const dataGroupGetter: DataGroupGetter<TeammateModel> = (model) => ({teammates: [model]});

  return findQueue(dispatch, getState, queueName, modelGetter, dataGroupGetter);
}

export function performMessageTaskAsyncAction(
  messageUid: string,
  task: TaskQueueItem<LoadedMessageModel>,
): ThunkAsyncAction<LoadedMessageModel | null> {
  return async (dispatch, getState) =>
    findMessageQueue(dispatch, getState, messageUid).performTaskAsync(task);
}

export function findMessageQueue(dispatch: Dispatch, getState: StateGetter, messageUid: string) {
  const queueName = buildMessageLockKey(messageUid);
  const modelGetter: ModelGetter<LoadedMessageModel> = (dataGroup, dataState) => {
    const messageResult = findLast(dataGroup.messages, (m) => m.uid === messageUid);
    const message = messageResult || dataState.messages[messageUid];
    if (!message || message.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
      return undefined;
    }

    return message;
  };
  const dataGroupGetter: DataGroupGetter<LoadedMessageModel> = (model) => ({messages: [model]});
  const deleteMessageCreator = () => deleteMessageAction(messageUid, Date.now());

  return findQueue(
    dispatch,
    getState,
    queueName,
    modelGetter,
    dataGroupGetter,
    (dataGroup) => partitionDataGroupByMessageUid(messageUid, dataGroup),
    deleteMessageCreator,
  );
}

export function findActivityQueue(dispatch: Dispatch, getState: StateGetter, activityId: number) {
  const queueName = buildActivityLockKey(activityId);
  const modelGetter: ModelGetter<ActivityModel> = (dataGroup, dataState) => {
    const activity = findLast(dataGroup.activities, (a) => a.id === activityId);
    return activity || dataState.activities[activityId];
  };
  const dataGroupGetter: DataGroupGetter<ActivityModel> = (model) => ({activities: [model]});
  const deleteActivityCreator = () => deleteActivity(activityId, Date.now());

  return findQueue(
    dispatch,
    getState,
    queueName,
    modelGetter,
    dataGroupGetter,
    undefined,
    deleteActivityCreator,
  );
}

export function findTagQueue(dispatch: Dispatch, getState: StateGetter, tagId: number) {
  const queueName = buildTagLockKey(tagId);
  const modelGetter: ModelGetter<TagModel> = (dataGroup, dataState) => {
    const tag = findLast(dataGroup.tags, (a) => a.id === tagId);
    return tag || dataState.tags[tagId];
  };
  const dataGroupGetter: DataGroupGetter<TagModel> = (model) => ({tags: [model]});
  const deleteTagCreator = () => deleteTagAction(tagId);

  return findQueue(dispatch, getState, queueName, modelGetter, dataGroupGetter, undefined, deleteTagCreator);
}

export function findVacationResponderQueue(
  dispatch: Dispatch,
  getState: StateGetter,
  vacationResponderId: number,
) {
  const queueName = buildVacationResponderLockKey(vacationResponderId);
  const modelGetter: ModelGetter<VacationResponderModel> = (dataGroup, dataState) => {
    const vacationResponder = findLast(dataGroup.vacationResponders, (t) => t.id === vacationResponderId);
    return vacationResponder || dataState.vacationResponders[vacationResponderId];
  };
  const dataGroupGetter: DataGroupGetter<VacationResponderModel> = (model) => ({vacationResponders: [model]});
  const deleteVacationResponderCreator = () => deleteVacationResponderAction(vacationResponderId);

  return findQueue(
    dispatch,
    getState,
    queueName,
    modelGetter,
    dataGroupGetter,
    undefined,
    deleteVacationResponderCreator,
  );
}
